import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough, 
  List, 
  ListOrdered, 
  Quote, 
  Link as LinkIcon, 
  Unlink, 
  Eraser, 
  Heading1, 
  Heading2 
} from 'lucide-react';

const isContentEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return stripped.length === 0;
};

const TinyRichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Write your announcement here...', 
  minHeight = '140px',
  required = false,
  className = ''
}) => {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    insertUnorderedList: false,
    insertOrderedList: false
  });
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Sync external value to editor HTML when it differs
  useEffect(() => {
    if (editorRef.current) {
      const currentHtml = editorRef.current.innerHTML;
      if (value !== currentHtml) {
        editorRef.current.innerHTML = value || '';
        setIsEmpty(isContentEmpty(value));
      }
    }
  }, [value]);

  const updateFormatStates = useCallback(() => {
    if (!document) return;
    try {
      setActiveFormats({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList')
      });
    } catch (e) {
      // Ignore queryCommandState errors in edge cases
    }
  }, []);

  const handleInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    const empty = isContentEmpty(html);
    setIsEmpty(empty);
    updateFormatStates();

    if (onChange) {
      // If visually empty, pass empty string for required validation
      onChange(empty ? '' : html);
    }
  };

  const executeCommand = (command, valueArg = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();

    if (command === 'createLink') {
      const selection = window.getSelection();
      const selectedText = selection.toString();
      const url = window.prompt('Enter link URL (e.g., https://example.com):', 'https://');
      if (url && url !== 'https://' && url.trim()) {
        document.execCommand('createLink', false, url.trim());
      }
    } else if (command === 'formatBlock') {
      // Check current format
      const currentBlock = document.queryCommandValue('formatBlock');
      if (currentBlock && currentBlock.toLowerCase() === valueArg.toLowerCase()) {
        document.execCommand('formatBlock', false, '<p>');
      } else {
        document.execCommand('formatBlock', false, `<${valueArg}>`);
      }
    } else {
      document.execCommand(command, false, valueArg);
    }

    handleInput();
  };

  const handleKeyDown = (e) => {
    // Basic keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        executeCommand('bold');
      } else if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        executeCommand('italic');
      } else if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        executeCommand('underline');
      }
    }
  };

  return (
    <div 
      className={`border rounded-xl bg-white transition-all overflow-hidden ${
        isFocused 
          ? 'border-[#2C5F7F] ring-2 ring-[#2C5F7F]/15 shadow-xs' 
          : 'border-gray-200 hover:border-gray-300'
      } ${className}`}
    >
      {/* Tiny Toolbar */}
      <div 
        className="flex flex-wrap items-center gap-0.5 p-1.5 bg-gray-50/80 border-b border-gray-100 select-none"
        onMouseDown={(e) => {
          // Prevent losing text selection focus when clicking toolbar buttons
          e.preventDefault();
        }}
      >
        <button
          type="button"
          onClick={() => executeCommand('bold')}
          className={`p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center ${
            activeFormats.bold 
              ? 'bg-[#2C5F7F] text-white shadow-xs' 
              : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
          }`}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('italic')}
          className={`p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center ${
            activeFormats.italic 
              ? 'bg-[#2C5F7F] text-white shadow-xs' 
              : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
          }`}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('underline')}
          className={`p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center ${
            activeFormats.underline 
              ? 'bg-[#2C5F7F] text-white shadow-xs' 
              : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
          }`}
          title="Underline (Ctrl+U)"
        >
          <Underline className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('strikeThrough')}
          className={`p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center ${
            activeFormats.strikeThrough 
              ? 'bg-[#2C5F7F] text-white shadow-xs' 
              : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
          }`}
          title="Strikethrough"
        >
          <Strikethrough className="w-3.5 h-3.5" />
        </button>

        <div className="w-[1px] h-4 bg-gray-200 mx-1" />

        <button
          type="button"
          onClick={() => executeCommand('formatBlock', 'h2')}
          className="p-1.5 rounded-lg transition-colors text-xs text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 flex items-center justify-center"
          title="Large Heading"
        >
          <Heading1 className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('formatBlock', 'h3')}
          className="p-1.5 rounded-lg transition-colors text-xs text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 flex items-center justify-center"
          title="Medium Heading"
        >
          <Heading2 className="w-3.5 h-3.5" />
        </button>

        <div className="w-[1px] h-4 bg-gray-200 mx-1" />

        <button
          type="button"
          onClick={() => executeCommand('insertUnorderedList')}
          className={`p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center ${
            activeFormats.insertUnorderedList 
              ? 'bg-[#2C5F7F] text-white shadow-xs' 
              : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
          }`}
          title="Bullet List"
        >
          <List className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('insertOrderedList')}
          className={`p-1.5 rounded-lg transition-colors text-xs flex items-center justify-center ${
            activeFormats.insertOrderedList 
              ? 'bg-[#2C5F7F] text-white shadow-xs' 
              : 'text-gray-600 hover:bg-gray-200/70 hover:text-gray-900'
          }`}
          title="Numbered List"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('formatBlock', 'blockquote')}
          className="p-1.5 rounded-lg transition-colors text-xs text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 flex items-center justify-center"
          title="Quote Block"
        >
          <Quote className="w-3.5 h-3.5" />
        </button>

        <div className="w-[1px] h-4 bg-gray-200 mx-1" />

        <button
          type="button"
          onClick={() => executeCommand('createLink')}
          className="p-1.5 rounded-lg transition-colors text-xs text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 flex items-center justify-center"
          title="Insert Link"
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('unlink')}
          className="p-1.5 rounded-lg transition-colors text-xs text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 flex items-center justify-center"
          title="Remove Link"
        >
          <Unlink className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => executeCommand('removeFormat')}
          className="p-1.5 rounded-lg transition-colors text-xs text-gray-600 hover:bg-gray-200/70 hover:text-gray-900 flex items-center justify-center ml-auto"
          title="Clear Formatting"
        >
          <Eraser className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Editable Area */}
      <div className="relative">
        {isEmpty && !isFocused && (
          <div 
            onClick={() => editorRef.current?.focus()}
            className="absolute left-4 top-3 text-sm text-gray-400 pointer-events-none select-none"
          >
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onFocus={() => {
            setIsFocused(true);
            updateFormatStates();
          }}
          onBlur={() => {
            setIsFocused(false);
            handleInput();
          }}
          onKeyUp={updateFormatStates}
          onMouseUp={updateFormatStates}
          onKeyDown={handleKeyDown}
          style={{ minHeight }}
          className="p-3.5 text-sm text-gray-800 focus:outline-none overflow-y-auto leading-relaxed max-h-[360px] 
            [&_p]:mb-2 [&_p:last-child]:mb-0
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5
            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5
            [&_li]:my-0.5
            [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-2 [&_h2]:mb-1
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-2 [&_h3]:mb-1
            [&_blockquote]:border-l-4 [&_blockquote]:border-[#2C5F7F]/40 [&_blockquote]:pl-3 [&_blockquote]:my-2 [&_blockquote]:italic [&_blockquote]:text-gray-600
            [&_a]:text-[#2C5F7F] [&_a]:underline [&_a]:font-medium
            [&_strong]:font-bold [&_b]:font-bold"
        />
      </div>

      {/* Footer hint */}
      <div className="px-3 py-1 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400">
        <span>Rich Text Editor</span>
        <span>Supports Bold (Ctrl+B), Lists, Headings, Links</span>
      </div>
    </div>
  );
};

export default TinyRichTextEditor;
